import { useEffect, useState } from "react";

import { Link, useNavigate } from "react-router";
import { Button, Card, Flex, Form, Grid, Input, Pagination, Select, Table, Tag } from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from 'date-fns';
import useTitleStore from "../../../store/titleStore";
import { useTableHeight } from "../../../hooks/useTableHeight";
import useDebounce from "../../../hooks/useDebounce";
import { indonesiaRegionService } from "../../../services/indonesia-region.service";
import { registrationService } from "../../../services/registration.service";
import RegistrationModalFilter from "../../../components/RegistrationModalFilter";
import RenderIf from "../../../components/RenderIf";
import { API_BASE_URL } from "../../../config/api.config";

export default function FinishRegistrationIndex(){

  const breakpoint = Grid.useBreakpoint()

  const navigate = useNavigate();

  const { setTitle } = useTitleStore();

  const tableHeight = useTableHeight();

  const [responsiveFilter, setResponsiveFilter] = useState(false)

  const [keyword, setKeyword] = useState("");
  const [selectedCity, setSelectedCity] = useState('')
  const [selectedDistrict, setSelectedDistrict] = useState('')
  const debouncedKeyword = useDebounce(keyword, 500);

  const [filterForm] = Form.useForm()

  const city = Form.useWatch('city', filterForm)
  const district = Form.useWatch('district', filterForm)
  const village = Form.useWatch('village', filterForm)

  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: []
  })

  useEffect(() => {
    setTitle("Data Terpasang");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams({
      ...dataParams,
      filter: [
        {
          operator: 'orWhere',
          value: [
            {
              field: 'nama_kustomer',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'nomor_kustomer',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'tracking_id',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'rt_kustomer',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'rw_kustomer',
              operator: 'like',
              value: debouncedKeyword
            },
          ]
        },
        {
          field: 'kota_kustomer',
          operator: 'like',
          value: city
        },
        {
          field: 'kecamatan_kustomer',
          operator: 'like',
          value: district
        },
        {
          field: 'kelurahan_kustomer',
          operator: 'like',
          value: village
        },
      ]
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword, city, district, village]);


  const { data: registrationData , isLoading, isRefetching } = useQuery({
    queryKey: ['registration', dataParams],
    queryFn: () => registrationService.getFinishData(dataParams),
    placeholderData: keepPreviousData,
  })

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const { data: cities } = useQuery({
    queryKey: ['cities'],
    queryFn: () => indonesiaRegionService.getCities()
  })

  const { data: districts } = useQuery({
    queryKey: ['districts', selectedCity],
    queryFn: () => indonesiaRegionService.getDistricts(selectedCity),
    enabled: !!selectedCity
  })

  const { data: villages } = useQuery({
    queryKey: ['districts', selectedDistrict],
    queryFn: () => indonesiaRegionService.getSubdistricts(selectedDistrict),
    enabled: !!selectedDistrict
  })

  useEffect(() => {

    filterForm.resetFields(['district', 'village'])

    if(city){
      const selected = cities.find(e => e.name == city)
      setSelectedCity(selected.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city])

  useEffect(() => {

    filterForm.resetFields(['village'])
    
    if(district){
      const selected = districts.find(e => e.name == district)
      setSelectedDistrict(selected.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [district])


  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      fixed: breakpoint.md && 'left',
      render: (text, record, index) =>  index + 1,
    },
    {
      title: 'Tracking ID',
      dataIndex: 'tracking_id',
      width: 150,
      fixed: breakpoint.md && 'left',
    },
    {
      title: 'Nomen/Norek',
      dataIndex: 'nomen',
      width: 150,
      fixed: breakpoint.md && 'left',
    },
    {
      title: 'Nama Pelanggan',
      dataIndex: 'name',
      width: 180
    },
    {
      title: 'No Telp',
      dataIndex: 'phone',
      width: 180
    },
    { 
      title: 'Kota',
      dataIndex: 'city',
      width: 180,
    },
    { 
      title: 'Kelurahan',
      dataIndex: 'village',
      width: 150,
    },
    { 
      title: 'RT/RW',
      width: 120,
      render: val => `${val.rt?.toString().padStart(3, '0')}/${val.rw?.toString().padStart(3, '0')}`
    },
    {
      title: 'Sumber',
      dataIndex: 'source',
      width: 150,
      render: val => {
        switch (val) {
          case 'surveyor':
            return  <Tag color="cyan" >Surveyor</Tag>
          case 'website':
            return  <Tag color="cyan" >Website</Tag>
        }
      }
    },
    {
      title: 'Tgl Registrasi',
      dataIndex: 'registrationDate',
      width: 150,
      fixed: 'left',
      render: val => val && format(new Date(val), 'dd/MM/yyyy - HH.mm')
    },
    {
      title: 'Tgl Terpasang',
      dataIndex: 'installedDate',
      width: 150,
      fixed: 'left',
      render: val => val && format(new Date(val), 'dd/MM/yyyy - HH.mm')
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: (record) => (
        <Flex justify="flex-end" gap={8} >
          <Link target="_blank" to={`${API_BASE_URL}/registration/export/${record.registrationId}`}>
            <Button  
              icon={<Icon icon="solar:download-square-bold-duotone" width="18" height="18" />}
              color="blue"
              variant="filled"
              size=""
            />
          </Link>
          <Button  
            icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
            color="primary"
            variant="filled"
            onClick={() => navigate('/registration-finish/detail/'+record.id)} 
            size=""
          />
        </Flex>
      )
    },
  ]

  return (
    <>
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <RenderIf when={breakpoint.lg} >
          <Form form={filterForm} >
            <Flex justify="flex-start" gap={20} style={{ paddingInline: 24, marginBottom: 24 }} >
              <Input 
                style={{ width: 250 }} 
                prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
                placeholder="cari.."
                onChange={e => setKeyword(e.target.value)}
                value={keyword}
              />
              <Form.Item noStyle name={'city'} >
                <Select
                  allowClear
                  options={cities?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kota"
                />
              </Form.Item>
              <Form.Item noStyle name={'district'} >
                <Select
                  allowClear
                  options={districts?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kecamatan"
                  disabled={!city}
                />
              </Form.Item>
              <Form.Item name={'village'} noStyle >
                <Select
                  allowClear
                  options={villages?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kelurahan"
                  disabled={!district}
                />
              </Form.Item>
            </Flex>
          </Form>
        </RenderIf>
        <RenderIf when={!breakpoint.lg} >
          <Flex justify="flex-end" gap={20} style={{ padding: '0 24px', marginBottom: 24 }} >
            <Button 
              variant="outlined" 
              color="primary" 
              icon={<Icon icon="mynaui:filter-solid" width="22" height="22" />}
              onClick={() => setResponsiveFilter(true)}
            >
              Filter
            </Button>
          </Flex>
        </RenderIf>
        <Table
          id="registration-table"
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={registrationData?.data}
          pagination={false}
          rowClassName={(record) => !record.is_open ? 'row-open' : ''}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={registrationData?.pagination?.total}
          showSizeChanger
          pageSizeOptions={['10', '20', '50']}
          current={registrationData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <RegistrationModalFilter
        form={filterForm}
        setKeyword={setKeyword}
        keyword={keyword}
        city={city}
        cities={cities}
        responsiveFilter={responsiveFilter}
        setResponsiveFilter={setResponsiveFilter}
        district={district}
        districts={districts}
        villages={villages}
      />
    </>
  )
}